import { authenticatedClient } from '../lib/client';
import { ROOT_ADMIN_ENDPOINTS } from './root-admin.utils';
import type { ApiResponse } from '../lib/types';
import type {
    RootUserInviteRequest,
    RootUserInviteResponse,
    RootUserListItem,
    OrganizationAccessGrantRequest,
    AccessGrantListResponse,
    PendingInvitation,
    VerifyUserRequest,
    VerifyUserResponse,
} from './root-admin.types';

// ==================== Developer Management ====================

/**
 * Invite a new developer to ROOT organization.
 * Only super_admin and manager can call this.
 * 
 * Creates:
 * 1. User document (status=inactive)
 * 2. ROOT organization membership with role=developer
 * 3. Pending invitation with signup token
 * 4. Sends email invitation
 */
export const inviteDeveloper = async (
    payload: RootUserInviteRequest
): Promise<ApiResponse<RootUserInviteResponse>> => {
    try {
        const response = await authenticatedClient.post(
            ROOT_ADMIN_ENDPOINTS.INVITE_DEVELOPER,
            { ...payload, role: 'developer' }
        );
        return { data: response.data };
    } catch (error: any) {
        return { error: error.response?.data?.message || 'Failed to invite developer' };
    }
};

/**
 * List all developers in ROOT organization.
 * Only super_admin and manager can call this.
 */
export const listDevelopers = async (): Promise<ApiResponse<RootUserListItem[]>> => {
    try {
        const response = await authenticatedClient.get(ROOT_ADMIN_ENDPOINTS.LIST_DEVELOPERS);
        return { data: response.data };
    } catch (error: any) {
        return { error: error.response?.data?.message || 'Failed to fetch developers' };
    }
};

// ==================== Manager Management ====================

/**
 * Invite a new manager to ROOT organization.
 * Only super_admin and manager can call this.
 * 
 * Creates:
 * 1. User document (status=inactive)
 * 2. ROOT organization membership with role=manager
 * 3. Pending invitation with signup token
 * 4. Sends email invitation
 */
export const inviteManager = async (
    payload: RootUserInviteRequest
): Promise<ApiResponse<RootUserInviteResponse>> => {
    try {
        const response = await authenticatedClient.post(
            ROOT_ADMIN_ENDPOINTS.INVITE_MANAGER,
            { ...payload, role: 'manager' }
        );
        return { data: response.data };
    } catch (error: any) {
        return { error: error.response?.data?.message || 'Failed to invite manager' };
    }
};

/**
 * List all managers in ROOT organization.
 * Only super_admin and manager can call this.
 */
export const listManagers = async (): Promise<ApiResponse<RootUserListItem[]>> => {
    try {
        const response = await authenticatedClient.get(ROOT_ADMIN_ENDPOINTS.LIST_MANAGERS);
        return { data: response.data };
    } catch (error: any) {
        return { error: error.response?.data?.message || 'Failed to fetch managers' };
    }
};

// ==================== Access Grants ====================

/**
 * Grant a developer/manager access to organizations.
 * Only super_admin and manager can call this.
 * 
 * Prerequisites:
 * - Target user must have ROOT membership
 * - Target user must be developer or manager role
 */
export const grantOrganizationAccess = async (
    userId: string,
    payload: OrganizationAccessGrantRequest
): Promise<ApiResponse<AccessGrantListResponse>> => {
    try {
        const response = await authenticatedClient.post(
            ROOT_ADMIN_ENDPOINTS.GRANT_ACCESS(userId),
            payload
        );
        return { data: response.data };
    } catch (error: any) {
        return { error: error.response?.data?.message || 'Failed to grant organization access' };
    }
};

/**
 * Revoke a developer/manager's access to an organization.
 * Only super_admin and manager can call this.
 */
export const revokeOrganizationAccess = async (
    userId: string,
    organizationId: string
): Promise<ApiResponse<string>> => {
    try {
        const response = await authenticatedClient.delete(
            ROOT_ADMIN_ENDPOINTS.REVOKE_ACCESS(userId, organizationId)
        );
        return { data: response.data };
    } catch (error: any) {
        return { error: error.response?.data?.message || 'Failed to revoke organization access' };
    }
};

/**
 * List all access grants for a user.
 * 
 * - super_admin/manager: can view anyone's grants
 * - developer/manager (viewing their own): can view their own grants
 */
export const listUserAccessGrants = async (
    userId: string
): Promise<ApiResponse<AccessGrantListResponse>> => {
    try {
        const response = await authenticatedClient.get(
            ROOT_ADMIN_ENDPOINTS.LIST_USER_ACCESS_GRANTS(userId)
        );
        return { data: response.data };
    } catch (error: any) {
        return { error: error.response?.data?.message || 'Failed to fetch access grants' };
    }
};

// ==================== Pending Invitations ====================

/**
 * List all pending ROOT organization invitations.
 * Only super_admin and manager can call this.
 */
export const listPendingRootInvitations = async (): Promise<ApiResponse<PendingInvitation[]>> => {
    try {
        const response = await authenticatedClient.get(ROOT_ADMIN_ENDPOINTS.LIST_PENDING_INVITATIONS);
        return { data: response.data };
    } catch (error: any) {
        return { error: error.response?.data?.message || 'Failed to fetch pending invitations' };
    }
};

/**
 * Cancel a pending ROOT organization invitation.
 * Only super_admin and manager can call this.
 */
export const cancelRootInvitation = async (
    invitationId: string
): Promise<ApiResponse<string>> => {
    try {
        const response = await authenticatedClient.delete(
            ROOT_ADMIN_ENDPOINTS.CANCEL_INVITATION(invitationId)
        );
        return { data: response.data };
    } catch (error: any) {
        return { error: error.response?.data?.message || 'Failed to cancel invitation' };
    }
};

// ==================== User Verification ====================

/**
 * Verify if a user exists and check their organization memberships.
 * Only super_admin and manager can call this.
 * 
 * Returns:
 * - If user doesn't exist: can be invited as new user
 * - If user exists: lists all their current organization memberships
 */
export const verifyUser = async (
    payload: VerifyUserRequest
): Promise<ApiResponse<VerifyUserResponse>> => {
    try {
        const response = await authenticatedClient.post(
            ROOT_ADMIN_ENDPOINTS.VERIFY_USER,
            payload
        );
        return { data: response.data };
    } catch (error: any) {
        return { error: error.response?.data?.message || 'Failed to verify user' };
    }
};

// ==================== User Management ====================

/**
 * Delete a developer/manager user entirely.
 * Only super_admin and manager can call this.
 * 
 * This will:
 * - Soft delete the user
 * - Soft delete their ROOT membership
 * - Soft delete all their organization access grants
 * - Soft delete any pending invitations
 * 
 * WARNING: This is a destructive operation!
 */
export const deleteRootUser = async (
    userId: string
): Promise<ApiResponse<string>> => {
    try {
        const response = await authenticatedClient.delete(
            ROOT_ADMIN_ENDPOINTS.DELETE_USER(userId)
        );
        return { data: response.data };
    } catch (error: any) {
        return { error: error.response?.data?.message || 'Failed to delete user' };
    }
};
